import { useState } from "react";

import { useTheme } from "@mui/material";
import { getMarketingReturnLabel } from "@/pages/channel-roi/constants/maps";
import { MROIColumnSelector } from "@/pages/channel-roi/constants/types";

import { LegendItem } from "../Legend";
import { useConfig } from "@/hooks/useConfig";

export type LegendOptions = ReturnType<typeof useColumnChartLegends>["legendOptions"];

export const useColumnChartLegends = (columnView: MROIColumnSelector) => {
  const theme = useTheme();
  const { config } = useConfig();
  const [showMarketingSpend, setShowMarketingSpend] = useState(true);
  const [showMarketingRevenue, setShowMarketingRevenue] = useState(true);
  const [showROI, setShowROI] = useState(true);
  const [showContribution, setShowContribution] = useState(true);
  const [showMarketingReturn, setShowMarketingReturn] = useState(true);
  const { revenueLabel } = config;

  const getLegendItems = (): LegendItem[] => {
    switch (columnView) {
      case MROIColumnSelector.SPEND_VS_REVENUE_AMOUNT:
      case MROIColumnSelector.SPEND_VS_REVENUE_PERCENTAGE:
        return [
          {
            id: "marketing-spend",
            title: "Marketing Spend",
            color: theme.palette.channelEffectivenessChart?.marketingSpend,
            isActive: showMarketingSpend,
            onChange: () => setShowMarketingSpend(!showMarketingSpend),
          },
          {
            id: "marketing-revenue",
            title: `Marketing ${revenueLabel}`,
            color: theme.palette.channelEffectivenessChart?.marketingRevenue,
            isActive: showMarketingRevenue,
            onChange: () => setShowMarketingRevenue(!showMarketingRevenue),
          },
        ];
      case MROIColumnSelector.ROI_PERCENTAGE:
        return [
          {
            id: "roi",
            title: "ROI",
            color: theme.palette.channelEffectivenessChart?.roi,
            isActive: showROI,
            onChange: () => setShowROI(!showROI),
          },
        ];
      case MROIColumnSelector.SHARE_OF_EFFECT_AMOUNT:
      case MROIColumnSelector.SHARE_OF_EFFECT_PERCENTAGE:
        return [
          {
            id: "contribution",
            title: "Contribution",
            color: theme.palette.channelEffectivenessChart?.shareOfEffect,
            isActive: showContribution,
            onChange: () => setShowContribution(!showContribution),
          },
        ];
      case MROIColumnSelector.MARKETING_RETURN_AMOUNT:
      case MROIColumnSelector.MARKETING_RETURN_PERCENTAGE:
        return [
          {
            id: "marketing-return",
            title: getMarketingReturnLabel(columnView),
            color: theme.palette.channelEffectivenessChart?.marketingReturn,
            isActive: showMarketingReturn,
            onChange: () => setShowMarketingReturn(!showMarketingReturn),
          },
        ];
      default:
        return [];
    }
  };

  const legendOptions = {
    showMarketingSpend,
    showMarketingRevenue,
    showROI,
    showContribution,
    showMarketingReturn,
  };

  return {
    getLegendItems,
    legendOptions,
  };
};
